import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { TranslatedCalling } from './common';
import Uploader from '~/components/ImageManager/Uploader';
import { useImages } from '~/context/image';
import { useUploadCompletionAck } from '~/hooks/useUploadCompletionAck';

function UploadInterface({
  onUploadComplete,
  uploadAckRetryKey,
}: {
  onUploadComplete?: (message: { role: string; content: string }) => boolean | void | Promise<boolean | void>;
  uploadAckRetryKey?: string;
}) {
  const { t } = useTranslation('chat');
  const { t: tArtwork } = useTranslation('artwork');
  const { uploadingImages, images } = useImages();
  const uploadingImagesList = Object.values(uploadingImages);

  const sessionImageIds = useRef<Set<string>>(new Set());
  const hasStartedUploading = useRef<boolean>(false);

  if (uploadingImagesList.length > 0) {
    hasStartedUploading.current = true;
  }

  useEffect(() => {
    uploadingImagesList.forEach((img) => {
      if (img.serverId) {
        sessionImageIds.current.add(img.serverId);
      }
    });
  }, [uploadingImagesList]);

  const sessionProcessedImages = images.filter(
    (img) => sessionImageIds.current.has(img.id) && img.status === 'processed'
  );
  const sessionImages = [...uploadingImagesList, ...sessionProcessedImages];

  const isUploadComplete =
    hasStartedUploading.current && uploadingImagesList.length === 0 && sessionProcessedImages.length > 0;
  const showUploader = !hasStartedUploading.current;

  useUploadCompletionAck({
    isUploadComplete,
    messageContent: tArtwork('upload.uploaded-message', { count: sessionProcessedImages.length }),
    uploadAckRetryKey,
    onUploadComplete,
  });

  if (isUploadComplete) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
        <CheckCircle className="w-4 h-4" />
        <span>{t('tool.upload-image.uploaded', { count: sessionProcessedImages.length })}</span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {sessionImages.length > 0 && (
        <div className="text-xs text-gray-400 font-light flex items-center gap-1 mb-2">
          {sessionImages.map((image) => {
            const imageSource = image.preview || (image as any).thumbnailUrl;
            const imageAlt = image.fileMeta?.fileName || (image as any).title || 'image';

            return (
              <div key={image.id} className="relative w-10 h-10 rounded-md overflow-hidden">
                {imageSource ? (
                  <img src={imageSource} alt={imageAlt} className="w-full h-full object-cover rounded-md" />
                ) : (
                  <div className="w-full h-full bg-gray-200 rounded-md" />
                )}

                {image.status === 'uploading' &&
                  typeof image.uploadProgress === 'number' &&
                  image.uploadProgress < 100 && (
                    <div className="absolute inset-0 bg-black/50 rounded-md flex flex-col items-center justify-center p-1">
                      <div className="w-full bg-gray-700 rounded-full h-0.5 overflow-hidden">
                        <div
                          className="bg-white h-0.5 rounded-full transition-all duration-300 ease-in-out"
                          style={{ width: `${image.uploadProgress || 0}%` }}
                        />
                      </div>
                    </div>
                  )}

                {['uploaded', 'processing'].includes(image.status || '') && (
                  <div className="absolute inset-0 rounded-md flex items-center justify-center bg-black/20">
                    <Loader2 className="w-3 h-3 text-white animate-spin" />
                  </div>
                )}

                {image.status === 'error' && (
                  <div className="absolute inset-0 rounded-md flex items-center justify-center bg-red-500/50">
                    <span className="text-white text-xs">!</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showUploader && (
        <div className="max-h-[400px] overflow-hidden">
          <Uploader
            source="chat_upload_tool"
            isOpen={true}
            onOpenChange={() => {}}
            isFullPage={true}
            showTitle={false}
            isOnboarding={false}
            isChat={true}
          />
        </div>
      )}
    </div>
  );
}

export const upload_image = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.common.thinking" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.common.thinking" />,
  result: ({
    onUploadComplete,
    uploadAckRetryKey,
  }: {
    onUploadComplete?: (message: { role: string; content: string }) => boolean | void | Promise<boolean | void>;
    uploadAckRetryKey?: string;
  }) => {
    return <UploadInterface onUploadComplete={onUploadComplete} uploadAckRetryKey={uploadAckRetryKey} />;
  },
};
